import React, { useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import api from '../services/api';
import './QuickPrintPage.css';

/**
 * QuickPrintPage — single text box + "Print" button for delivery orders.
 *
 * The user types the order in free text and presses Print. The backend parses it,
 * registers the sale and returns a public id; the delivery slip is then printed in
 * a hidden iframe so the user never leaves this screen.
 */
const QuickPrintPage = () => {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);
    const [printSrc, setPrintSrc] = useState(null);
    const textareaRef = useRef(null);

    const placeholder = t(
        'quickPrintPage.placeholder',
        'Cliente: Ana Gómez\nDirección: Calle 10 #15-20, Tuluá\nTeléfono: 2245678\nCelular: 310 555 1234\nPago: Cancela con $50.000 una compra de $28.000.'
    );

    const requestQuickPrint = (forceWithoutPrice) =>
        api.post('/sales/quick-print', { text, forceWithoutPrice }).then((r) => r.data);

    const handlePrint = async () => {
        if (!text.trim()) return;
        setLoading(true);
        try {
            let data = await requestQuickPrint(false);

            // Backend asks to confirm printing without a price.
            if (data.needsPrice) {
                const result = await Swal.fire({
                    title: t('quickPrintPage.noPrice.title', '¿Generar recibo sin precio?'),
                    text: t('quickPrintPage.noPrice.text', 'No detectamos un valor en el pedido. Puedes imprimirlo de todas formas con total $0.'),
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: t('quickPrintPage.noPrice.confirm', 'Sí, imprimir'),
                    cancelButtonText: t('quickPrintPage.noPrice.cancel', 'Cancelar'),
                    confirmButtonColor: 'var(--ev-action-primary-bg)',
                });
                if (!result.isConfirmed) {
                    setLoading(false);
                    return;
                }
                data = await requestQuickPrint(true);
            }

            // Print the delivery slip in a hidden iframe (it auto-calls window.print()).
            // The timestamp forces a remount so the auto-print fires on every order.
            setPrintSrc(`/delivery-slip/${data.public_id}?print=true&ts=${Date.now()}`);
            setText('');
            toast.success(
                t('quickPrintPage.success', 'Pedido #{{invoice}} registrado e impreso', { invoice: data.invoice_number })
            );
            textareaRef.current?.focus();
        } catch (err) {
            const status = err.response?.status;
            if (status === 403 && err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error(t('quickPrintPage.readError', 'No pudimos leer el pedido. Revisa el texto e inténtalo de nuevo.'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6}>
                    <h2 className="mb-1">{t('quickPrintPage.title', 'Imprimir Pedido')}</h2>
                    <p className="text-muted">
                        {t('quickPrintPage.subtitle', 'Escribe el pedido y presiona Imprimir. La venta se registra automáticamente.')}
                    </p>

                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    as="textarea"
                                    rows={7}
                                    className="quick-print-textarea"
                                    ref={textareaRef}
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={placeholder}
                                    disabled={loading}
                                    autoFocus
                                />
                            </Form.Group>
                            <div className="d-grid">
                                <Button
                                    variant="dark"
                                    size="lg"
                                    onClick={handlePrint}
                                    disabled={loading || !text.trim()}
                                >
                                    {loading ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />{t('quickPrintPage.printing', 'Imprimiendo...')}</>
                                    ) : (
                                        t('quickPrintPage.printButton', 'Imprimir')
                                    )}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Hidden print frame — keeps the user on this screen */}
            {printSrc && (
                <iframe
                    key={printSrc}
                    title="quick-print"
                    src={printSrc}
                    style={{ position: 'absolute', width: 0, height: 0, border: 0, visibility: 'hidden' }}
                />
            )}
        </Container>
    );
};

export default QuickPrintPage;
